import {gql} from '@apollo/client';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {Tag} from 'folder-items/FileConstants';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import TagValueViewer from 'metadata/TagValueViewer';
import {getSharedSmartChipProps, SharedSmartChipProps} from 'ui-library/chips/ChipUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

import {BulkChipItemPrefetchQuery} from './__generated/BulkChipItemPrefetchQuery';
import {StudyChipPopoverQuery, StudyChipPopoverQueryVariables} from './__generated/StudyChipPopoverQuery';
import {ChipSize} from './Chip';
import ChipPopoverContent from './ChipPopoverContent';
import {generateKeyForSchemaField} from './ChipPopoverContentUtils';
import {NO_ACCESS_CHIP_TEXT} from './NoAccessChip';
import QueryingChipPopoverContent from './QueryingChipPopoverContent';
import SmartChip from './SmartChip';
import useSmartChipQuery, {SmartChipType} from './useSmartChipQuery';

type ChipStudy = ExtractByTypename<BulkChipItemPrefetchQuery['studies'][number], 'StudyNode'>;

type PopoverStudy = StudyChipPopoverQuery['study'];

export const STUDY_CHIP_POPOVER_QUERY = gql`
  query StudyChipPopoverQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        name
        humanId
        studySchema {
          id
          name
        }
        tagsJson
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function StudyChipPopoverContent({study}: {study: PopoverStudy}): JSX.Element | null {
  if (isInaccessible(study)) {
    return <>{NO_ACCESS_CHIP_TEXT}</>;
  }
  const fields = [
    {
      label: 'Name',
      value: (
        <>
          <BenchlingIcon icon={ModelTypeConstants.STUDY} /> &nbsp; {study.name}
        </>
      ),
    },
    {label: 'ID', value: <>{study.humanId}</>},
    {label: 'Schema', value: <>{study.studySchema.name}</>},
    ...(study.tagsJson as Array<Tag>).map((tag) => ({
      generateStableKey: () => generateKeyForSchemaField(tag),
      label: tag.name,
      value: (
        <TagValueViewer
          tag={tag}
          chipSize={ChipSize.MINI}
          shouldPreviewImages={true}
          showChipsAsLinks={true}
          summarize={true}
          summarizeThreshold={3}
        />
      ),
    })),
  ];
  return <ChipPopoverContent info={{fields}} />;
}

export function QueryingStudyChipPopoverContent({id}: {id: string}): JSX.Element {
  return (
    <QueryingChipPopoverContent<StudyChipPopoverQuery, StudyChipPopoverQueryVariables>
      variables={{studyId: id}}
      query={STUDY_CHIP_POPOVER_QUERY}
    >
      {(data: StudyChipPopoverQuery) => <StudyChipPopoverContent study={data.study} />}
    </QueryingChipPopoverContent>
  );
}

export interface StudyChipProps extends SharedSmartChipProps<SmartChipType.STUDY> {}

function getSecondaryText(study: ChipStudy): string {
  return study.humanId;
}

export default function StudyChip(props: StudyChipProps): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;

  const {
    item: study,
    handleClickLink,
    isInaccessible,
    itemUrl: studyUrl,
  } = useSmartChipQuery<SmartChipType.STUDY>({
    id,
    onClick,
    getUrlFn: (study) => study.editUrl,
  });
  const onClickHandler = (e: BasicEvent): void => {
    if (study) {
      if (onClick != null) {
        handleClickLink(e);
      } else {
        FileBrowserActions.openFile(study.id);
      }
    }
  };
  return (
    <SmartChip
      {...sharedSmartChipProps}
      icon={ModelTypeConstants.STUDY}
      isInaccessible={isInaccessible}
      item={study}
      onClick={onClickHandler}
      url={studyUrl}
      popoverContentOverride={id ? <QueryingStudyChipPopoverContent id={id} /> : null}
      getSecondaryText={getSecondaryText}
    />
  );
}
